import React, { useState, useEffect, useRef } from "react";
import { TableContainer, Paper, Typography, Box, PaginationItem } from "@mui/material";
import { api, get } from "../../../../utils/api";
import { styled } from "@mui/material/styles";
import DateRangePickerComponent from "../../Roles and Permissions/Filters/Date";
import PaginationBox from "../../../../Components/Masters/Pagination";
import DeleteDialog from "../../../../Components/Masters/DeleteDialog";
import ColumnHeadings from "../../../../Components/Masters/ColumnHeadings";
import MenuActions from "../../../../Components/Masters/MenuActions";
import TableContent from "../../../../Components/Masters/TableContent";
import FilterDrawer from "../../../../Components/Masters/FilterDrawer";
import TopMenu from "../../../../Components/Masters/TopMenu";
import MessagePopup from "../../../../Components/Masters/MessagePopup";
import TableHeading from "../../../../Components/Masters/TableHeading";
import MenuIcon from "../../../../Components/Masters/MenuIcon";
import NoDataFound from "../../../../Components/Masters/NoDataFound";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import MastersSidebar from "../../../../Layout/MastersSidebar";
import Unauthorized from "../../../../Components/Unauthorized/Unauthorized";
import AddCustomerMaster from "./AddCustomerMaster";
import ViewCustomerMaster from "./ViewCustomerMaster";

const StyledPaginationItem = styled(PaginationItem)(() => ({
  "&.Mui-selected": {
    backgroundColor: "green",
    color: "#fff",
    fontWeight: "bold",
  },
  "&:hover": {
    backgroundColor: "#ededed",
  },
}));

const formInputData = [
  {
    fieldName: "customerVendorName",
    title: "Customer/Vendor Name",
    placeholder: "Enter Customer/Vendor Name",
  },
  {
    fieldName: "productName",
    title: "Product Name",
  },
  {
    fieldName: "picName",
    title: "PIC Name",
    placeholder: "Enter PIC Name",
  },
  {
    fieldName: "country",
    title: "Country",
  },
  {
    fieldName: "email",
    title: "Email",
    placeholder: "Enter Email",
  },
  {
    fieldName: "processingMethodsUsed",
    title: "Processing methods used",
    placeholder: "Enter Processing methods used",
  },
];

const viewInputData = [
  { fieldName: "customerVendorName", title: "Customer/Vendor Name" },
  { fieldName: "productName", title: "Product Name" },
  { fieldName: "picName", title: "PIC Name" },
  { fieldName: "country", title: "Country" },
  { fieldName: "email", title: "Email" },
  { fieldName: "processingMethodsUsed", title: "Processing methods used" },
];

const CustomerMaster = () => {
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [addData, setAddData] = useState(false);
  const [viewData, setViewData] = useState(false);
  const [element, setElement] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("Date");
  const [drawerFilterOpen, setDrawerFilterOpen] = useState(false);
  const [tempFilter, setTempFilter] = useState({
    startDate: null,
    endDate: null,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const [rowsPerPage] = useState(10);
  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const filters = ["Date"];
  const appliedFilters = { Date: !!(startDate || endDate) };
  const selectedFields = {};
  const setSelectedFields = () => {};
  const errors = { Date: false };
  const setErrors = () => {};

  const filtersToApply = {
    startDate: tempFilter?.startDate ? tempFilter?.startDate.format("YYYY-MM-DD") : "",
    endDate: tempFilter?.endDate ? tempFilter?.endDate.format("YYYY-MM-DD") : "",
  };

  const fetchData = async (filtersArg = isFiltersApplied ? filtersToApply : {}) => {
    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(rowsPerPage),
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction,
        ...Object.fromEntries(
          Object.entries(filtersArg).filter(([, v]) => v !== "" && v !== undefined && v !== null),
        ),
      }).toString();

      const response = await get(`/master/customerMasters?${queryParams}`);
      setData(response?.data?.data || []);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching customer master:", error);
      setData([]);
      setTotalPages(1);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, sortConfig]);

  const updatePage = () => {
    if (page > 1 && data.length === 1) {
      setPage(page - 1);
    }
    fetchData();
  };

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setElement(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    setAddData(true);
    setViewData(false);
  };

  const handleView = () => {
    handleMenuClose();
    setViewData(true);
    setAddData(false);
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialog(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchData(filtersToApply);
    setStartDate(tempFilter?.startDate);
    setEndDate(tempFilter?.endDate);
    setIsFiltersApplied(!!(tempFilter?.startDate || tempFilter?.endDate));
    setDrawerFilterOpen(false);
  };

  const handleResetFilters = async () => {
    setTempFilter({ startDate: null, endDate: null });
    setStartDate(null);
    setEndDate(null);
    setPage(1);
    setIsFiltersApplied(false);
    await fetchData({});
    setDrawerFilterOpen(false);
  };

  const handleFilterDrawer = () => {
    setTempFilter({
      startDate: startDate,
      endDate: endDate,
    });
    setDrawerFilterOpen(false);
  };

  const handleSort = (column, direction) => {
    setSortConfig({ key: column, direction });
  };

  const columnHeadings = [
    { fieldName: "createdAt", title: "Date" },
    { fieldName: "customerVendorName", title: "Customer/Vendor Name" },
    // { fieldName: "productName", title: "Product Name" },
    { fieldName: "picName", title: "PIC Name" },
    { fieldName: "country", title: "Country" },
    { fieldName: "email", title: "Email" },
    // { fieldName: "processingMethodsUsed", title: "Processing methods used" },
  ];

  const renderSelectedFilter = () => {
    return (
      <DateRangePickerComponent
        startDate={tempFilter?.startDate}
        endDate={tempFilter?.endDate}
        setStartDate={(date) => setTempFilter((prev) => ({ ...prev, startDate: date }))}
        setEndDate={(date) => setTempFilter((prev) => ({ ...prev, endDate: date }))}
        setErrors={setErrors}
        errors={errors.Date}
      />
    );
  };

  const tableContainerRef = useRef(null);
  const [isShortContent, setIsShortContent] = useState(false);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const tableHeight = container.scrollHeight;
      const screenHeight = window.innerHeight;
      setIsShortContent(tableHeight < screenHeight / 1.5);
    }
  }, [data]);

  const getFieldData = (item, fieldName) => {
    if (fieldName === "productName") {
      return item?.productName?.productName || item?.productName?.name || item?.productName || "-";
    }
    if (fieldName === "processingMethodsUsed") {
      const value = item?.processingMethodsUsed;
      return Array.isArray(value) ? value.join(", ") : value || "-";
    }
    return item?.[fieldName];
  };

  const getDateFieldForColumn = (item, el) => {
    if (el.fieldName === "createdAt") return item.createdAt;
    return false;
  };

  return (
    <MainDashboardLayout sidebar={<MastersSidebar />}>
      <Unauthorized requiredModule="Setup | Master">
        <Box sx={{ backgroundColor: "#ededed", width: "100%" }}>
          <MessagePopup {...{ snackOpen, setsnackOpen }} />
          <AddCustomerMaster
            id={element ? element._id : ""}
            addData={addData}
            setAddData={setAddData}
            element={element}
            addUrl="/master/customerMasters"
            updateUrl="/master/customerMasters"
            fetchData={fetchData}
            setsnackOpen={setsnackOpen}
            formInputData={formInputData}
          />
          <ViewCustomerMaster
            data={element}
            viewData={viewData}
            setViewData={setViewData}
            inputData={viewInputData}
            handleEdit={handleEdit}
          />
          {deleteDialog && (
            <DeleteDialog
              fetchData={updatePage}
              {...{
                deleteDialog,
                setDeleteDialog,
                id: element?._id,
                url: `${api}/master/customerMasters`,
                method: "delete",
                setsnackOpen,
              }}
            />
          )}
          {drawerFilterOpen && (
            <FilterDrawer
              {...{
                renderSelectedFilter,
                drawerFilterOpen,
                setDrawerFilterOpen,
                handleApplyFilters,
                handleFilterDrawer,
                setSelectedFilter,
                handleResetFilters,
                selectedFilter,
                filters,
                appliedFilters,
                selectedFields,
                setSelectedFields,
              }}
            />
          )}
          <TopMenu
            setData={setElement}
            setAddData={setAddData}
            {...{
              heading: "Scope 3",
              setDrawerFilterOpen,
              filterApplied: isFiltersApplied,
            }}
          />
          <TableContainer component={Paper} ref={tableContainerRef} sx={{ maxWidth: "100%" }}>
            <Box>
              <TableHeading heading={"Customer Master"} />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "12% 17% 17% 20% 25% 8%",
                  borderBottom: "1px solid #ddd",
                  borderTop: "1px solid #ddd",
                }}
              >
                {columnHeadings.map((el) => (
                  <ColumnHeadings
                    key={el.fieldName}
                    {...{
                      title: el.title,
                      key: el.title,
                      handleSort,
                      field: el.fieldName,
                      sortConfig,
                    }}
                  />
                ))}
                <Typography sx={{ fontWeight: "bold", padding: "8px 12px", textAlign: "center" }}>
                  Action
                </Typography>
              </Box>
              {data.length === 0 ? (
                <NoDataFound />
              ) : (
                data.map((item) => (
                  <Box
                    key={item._id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "12% 17% 17% 20% 25% 8%",
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    {columnHeadings.map((el) => (
                      <TableContent
                        key={el.fieldName}
                        {...{
                          fieldData: getFieldData(item, el.fieldName),
                          dateField: getDateFieldForColumn(item, el),
                        }}
                      />
                    ))}
                    <MenuIcon {...{ handleMenuClick, field: item }} />
                  </Box>
                ))
              )}
            </Box>
          </TableContainer>
          {totalPages > 1 && (
            <PaginationBox
              isShortContent={isShortContent}
              {...{ totalPages, page, handleChangePage, StyledPaginationItem }}
            />
          )}
          <MenuActions
            {...{
              id: element?._id,
              handleView,
              handleEdit,
              handleDelete,
              anchorEl,
              handleMenuClose,
            }}
          />
        </Box>
      </Unauthorized>
    </MainDashboardLayout>
  );
};

export default CustomerMaster;
